import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import { selectCurrentCompany, selectPortalPermissions, selectUserIsTeamResponsible } from '../../state/user/user.reducer';
import { Actions, ofType } from '@ngrx/effects';
import { UserActions } from '@state/user/user.actions';

/**
 * @description This service is used to check if the user has the required permissions. Used in the permissions directive and route permission guard, but can also be used separately.
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  permissionChange$ = new ReplaySubject<void>();
  role$ = this.store
    .select(selectCurrentCompany)
    .pipe(map((company) => company?.role));

  private permissionsLoaded = new BehaviorSubject<boolean>(false);
  permissionsLoaded$ = this.permissionsLoaded.asObservable();

  private _licensePermissions: string[] = [];
  private _portalPermissions: string[] | null = null;
  private _isTeamResponsible: boolean | null = null;

  constructor(private store: Store, private actions$: Actions) {
    combineLatest([
      this.store.select(selectCurrentCompany),
      this.store.select(selectPortalPermissions),
      this.store.select(selectUserIsTeamResponsible),
    ]).subscribe(([company, portalPermissions, isTeamResponsible]) => {
      this._licensePermissions = company?.licencePermissions || [];
      this._portalPermissions = portalPermissions;
      this._isTeamResponsible = isTeamResponsible;
      this.permissionChange$.next();
      this.permissionsLoaded.next(
        isTeamResponsible !== null
        && portalPermissions !== null
      );
    });
  }

  get licensePermissions() {
    return this._licensePermissions;
  }

  get portalPermissions() {
    return this._portalPermissions;
  }

  get isTeamResponsible() {
    return this._isTeamResponsible;
  }

  /**
   * @description This function is used to check if the user has ALL of the required permissions.
   * @example
   * this.permissionsService.checkPermissionStrict(['CHATS.VIEW', 'CHATS.EDIT', 'CHATS.DELETE', 'ADMIN']);
   * @param permission - List of permission to check. Can also include roles and booleans (used in individual rows in tables for example).
   * @returns boolean
   */
  checkPermissionStrict(
    permission: (string | boolean)[],
  ): boolean {
    return permission.every((p) => {
      switch (typeof p) {
        case 'string':
          return this._portalPermissions.includes(p);
        case 'boolean':
          return p;
        default:
          return false;
      }
    });
  }

  /**
   * @description This function is used to check if the user has SOME of the required permissions.
   * @example
   * this.permissionsService.checkPermissions(['CHATS.VIEW', 'CHATS.EDIT', 'CHATS.DELETE', 'ADMIN']);
   * @param permission - List of permission to check. Can also include roles and booleans (used in individual rows in tables for example).
   * @returns boolean
   */
  checkPermissions(permission: (string | boolean)[]): boolean {
    return permission.some((p) => {
      switch (typeof p) {
        case 'string':
          return this._portalPermissions.includes(p);
        case 'boolean':
          return p;
        default:
          return false;
      }
    });
  }

  /**
   * @description This function is used to check if the user has ALL of the required license permissions.
   * @example
   * this.permissionsService.checkLicensePermissions(['CHATS.VIEW', 'CHATS.EDIT', 'CHATS.DELETE']);
   * @param permission - The permission to check
   * @param mode - 'all' | 'any':
   *  - 'all' (default): returns true if the user has all the license permissions.
   *  - 'any': returns true if the user has at least one of the license permissions.
   * @returns boolean
   */
  checkLicensePermissions(permission: string[], mode: 'all' | 'any' = 'all'): boolean {
    if (mode === 'all') {
      return permission.every((p) => this._licensePermissions.includes(p));
    }

    return permission.some((p) => this._licensePermissions.includes(p));
  }

  checkPermissionsAndLicenses(permissions: string[], mode: 'all' | 'any' = 'all'): boolean {
    const userHasPermissions = mode === 'all'
      ? this.checkPermissionStrict(permissions)
      : this.checkPermissions(permissions);

    return userHasPermissions && this.checkLicensePermissions(permissions, mode)
  }
}
